import React, { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
// @ts-ignore
import v4 from 'uuid/v4';

import { Target } from '../../../../client';
import { ParentTriggererContext } from '../../../../contexts/ParentTriggererContext';
import theme from '../../../../styled-components/dark';
import { type CardType } from '../../../../types/card';
import { MatchViewModes, type MatchViewMode } from '../../../../types/matchView';
import { SportIds, type SportId } from '../../../../types/sports';
import { normalize } from '../../../../utils/number';
import LineUpRugbyCardIconSvgAsComponent from '../../assets/asComponents/LineUpRugbyCardIconSvgAsComponent';
import LineUpSoccerCardIconSvgAsComponent from '../../assets/asComponents/LineUpSoccerCardIconSvgAsComponent';
import { LineUpArrowInIconSvg, LineUpArrowOutIconSvg } from '../../assets/svg';
import { CardIconsByCardType } from '../../constants/cards';
import LineUpGoalsInfo from '../LineUpGoalsInfo/LineUpGoalsInfo';
import { LineUpPlayerJerseySize } from '../LineUpPlayerJersey/LineUpPlayerJersey';
import LineUpPlayerJerseyContainer from '../LineUpPlayerJersey/LineUpPlayerJerseyContainer';
import LineUpPlayerOddsContainer from '../LineUpPlayerOdds/LineUpPlayerOddsContainer';
import LineUpPlayerStatsContainer from '../LineUpPlayerStats/LineUpPlayerStatsContainer';

type SIZESType = {
  fontSize: string;
  letterSpacing: string;
  maxWidth: string;
  cards: {
    transform: string;
    fontSize: string;
  };
};

const SIZES: Record<LineUpPlayerJerseySize, SIZESType> = {
  [LineUpPlayerJerseySize.small]: {
    fontSize: '75%',
    letterSpacing: '0.06em',
    maxWidth: '8em',
    cards: {
      transform: 'translateY(0.2em)',
      fontSize: '120%',
    },
  },
  [LineUpPlayerJerseySize.medium]: {
    fontSize: '100%',
    letterSpacing: '0.08em',
    maxWidth: '7.2em',
    cards: {
      transform: 'unset',
      fontSize: '100%',
    },
  },
  [LineUpPlayerJerseySize.large]: {
    fontSize: '115%',
    letterSpacing: '0.08em',
    maxWidth: '7.2em',
    cards: {
      transform: 'unset',
      fontSize: '100%',
    },
  },
};

const JerseySizesBySportId = new Proxy<Partial<Record<SportId, LineUpPlayerJerseySize>>>(
  {
    [SportIds.RUGBY]: LineUpPlayerJerseySize.small,
    [SportIds.RUGBY_LEAGUE]: LineUpPlayerJerseySize.small,
  },
  {
    get(origin, key) {
      const numericKey = Number(key) as SportId;
      if (!origin[numericKey]) {
        return LineUpPlayerJerseySize.medium;
      }
      return origin[numericKey];
    },
  },
);

const CARD_WIDTH = 0.8; // em

type CardIconType = {
  $index: number;
  $maxIndex: number;
};

const CardIcon = styled.svg<CardIconType>`
  position: absolute;
  width: ${CARD_WIDTH}em;
  height: 1.1em;
  left: 0;
  top: 0;

  ${({ $index, $maxIndex }) => {
    const startingPositionAngle = -75; // deg
    const arcAngleLength = Math.min(360, $maxIndex / (360 * 0.0002)); // deg
    const [minRotateZAngle, maxRotateZAngle] = [
      startingPositionAngle,
      Math.min(startingPositionAngle + 360, startingPositionAngle + arcAngleLength),
    ];
    const [minRotateZRadAngle, maxRotateZRadAngle] = [
      (minRotateZAngle / 360) * 2 * Math.PI,
      (maxRotateZAngle / 360) * 2 * Math.PI,
    ];
    const radius = CARD_WIDTH * 1.65;
    const radAngle = $maxIndex ? ($index / $maxIndex) * 2 * Math.PI : 2 * Math.PI;

    const normalizedRadAngle = normalize(radAngle, 0, 2 * Math.PI, minRotateZRadAngle, maxRotateZRadAngle);
    const [translateX, translateY] = [radius * Math.cos(normalizedRadAngle), radius * Math.sin(normalizedRadAngle)];

    return css`
      transform-origin: center center;
      transform: translate(calc(${translateX}em - 50%), calc(${translateY}em - 50%))
        rotateZ(${(normalizedRadAngle * 180) / Math.PI + 90}deg);
    `;
  }}
`;

type CardProps = {
  cardType: CardType;
  index: number;
  nbCards: number;
};

const Card = ({ cardType, index, nbCards }: CardProps) => {
  const Icon = useMemo(() => CardIconsByCardType[cardType], [cardType]);

  return <CardIcon key={cardType} as={Icon} $index={index} $maxIndex={nbCards - 1} />;
};

const hasJustChangedAnimation = keyframes`
  50% {
    transform: scale(2);
  }
`;

type hasJustChangedSharedStyleType = {
  $hasJustChanged: boolean;
};

const hasJustChangedSharedStyle = css<hasJustChangedSharedStyleType>`
  ${({ $hasJustChanged }) => css`
    ${$hasJustChanged
      ? css`
          animation: ${hasJustChangedAnimation} 1000ms ease-in-out;
        `
      : undefined}
  `}
`;

type WrapperType = {
  $jerseySize: LineUpPlayerJerseySize;
  $isDisabled: boolean;
};

const Wrapper = styled.div<WrapperType>`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  width: 100%;

  max-width: ${({ $jerseySize }) => SIZES[$jerseySize].maxWidth};

  font-size: ${Target.__MOBILE__ ? 0.65 : 0.7}em;
  text-align: center;
  color: white;
  cursor: ${({ $isDisabled }) => ($isDisabled ? 'default' : 'pointer')};

  display: flex;
  align-items: center;
  justify-content: center;

  user-select: none;

  ${({ $isDisabled }) =>
    $isDisabled || Target.__MOBILE__
      ? undefined
      : css`
          transition: transform 150ms ease-in-out;

          &:hover {
            transform: scale(1.07);
          }
        `}

  pointer-events: var(--OddButton-pointerEvents, auto);
`;

const JerseyRow = styled.div`
  position: relative;
  display: inline-flex;
  justify-content: center;
  align-items: flex-end;
  margin-top: 1em;

  @keyframes onChangeKeyframes {
    50% {
      transform: scale(2);
    }
  }
`;

const SubstitutionInfosArea = styled.div`
  position: absolute;
  left: -0.1em;
  transform: translateX(-100%);
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  justify-content: flex-start;
  color: ${theme.LineUpPlayerSubtitutionInfosArea.color};

  margin-bottom: -0.2em;

  > *:not(:last-child) {
    padding: 0 0.1em;
    margin-top: -0.3em;
  }

  > *:last-child {
    padding: 0 0.1em 0 0;
  }
`;

const SubstitutionTimerBlock = styled.div`
  position: relative;

  display: flex;
  flex-direction: column;
  align-items: center;

  ${hasJustChangedSharedStyle}
`;

const SubstitutionTimerLabel = styled.div`
  margin-bottom: -0.2em;
`;

const StyledLineUpArrowInIconSvg = styled(LineUpArrowInIconSvg)``;

const StyledLineUpArrowOutIconSvg = styled(LineUpArrowOutIconSvg)``;

type PlayerWrapperType = {
  $nameSize: LineUpPlayerJerseySize;
};

const PlayerWrapper = styled.div<PlayerWrapperType>`
  width: 108%;
  position: relative;
  height: 1.8em;
  margin-top: -0.1em;

  ${({ $nameSize }) => css`
    font-size: ${SIZES[$nameSize].fontSize};
    letter-spacing: ${SIZES[$nameSize].letterSpacing};
  `}

  text-overflow: ellipsis;
`;

const PlayerName = styled.div`
  position: absolute;
  left: 50%;
  width: 100%;
  line-height: 1.8em;
  transform: translateX(-50%);

  overflow: hidden;
  white-space: nowrap;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: 0.08em;
`;

const StyledSoccerPlayerCardIcon = styled(LineUpSoccerCardIconSvgAsComponent)`
  position: relative;
  height: 100%;
  width: 100%;
`;

const StyledRugbyPlayerCardIcon = styled(LineUpRugbyCardIconSvgAsComponent)`
  position: relative;
  height: 100%;
  width: 100%;
`;

const PlayerArea = styled.div`
  position: absolute;
  top: 0;
  height: 80%;
  width: 85%;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
`;

const StyledLineUpPlayerJerseyContainer = styled(LineUpPlayerJerseyContainer)``;

type CardsWrapperType = {
  $size: LineUpPlayerJerseySize;
} & hasJustChangedSharedStyleType;

const CardsWrapper = styled.div<CardsWrapperType>`
  position: absolute;
  top: 50%;
  left: 70%;
  transform: translate(-50%, -50%);

  ${({ $size }) => css`
    font-size: ${SIZES[$size].cards.fontSize};
    transform: ${SIZES[$size].cards.transform};
  `}

  ${hasJustChangedSharedStyle}
`;

const StyledLineUpPlayerOddsContainer = styled(LineUpPlayerOddsContainer)`
  width: 100%;
  font-size: 110%;
  margin-top: 0.1em;
  padding: 0 0.6em;

  & .odd-button-wrapper {
    line-height: 2.2em;
    padding: 0;
    font-size: 1em;
    min-height: 2.3em;
  }

  & .odds-update-indicator {
    font-size: 75%;
    right: 0.4em;
  }

  & .bet-group-outcome-odd {
    position: relative;
  }

  & .bet-group-outcome-odd-chip {
    position: absolute;
    margin-bottom: 0;
    top: 175%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  & .my-match-logo-wrapper {
    left: 0;
    top: 50%;
    transform: translate(-50%, -50%) scale(1.1);
  }
`;

const animateOnChange = (
  timeoutRef: MutableRefObject<NodeJS.Timeout | null>,
  justChangedSetter: (next: boolean) => void,
) => {
  if (timeoutRef && justChangedSetter) {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    justChangedSetter(true);

    timeoutRef.current = setTimeout(() => {
      justChangedSetter(false);
    }, 1000);
  }
};

type LineUpPlayerProps = {
  className?: string;
  name?: string;
  canSelect: boolean;
  sportId: SportId;
  selected?: boolean;
  nbGoals?: number;
  nbOwnGoals?: number;
  enterTimer?: string;
  leaveTimer?: string;
  cards?: Array<CardType>;
  matchViewMode: MatchViewMode;
};

const LineUpPlayer = ({
  className = '',
  name = '',
  canSelect,
  sportId,
  selected,
  nbGoals,
  nbOwnGoals,
  enterTimer,
  leaveTimer,
  cards,
  matchViewMode,
}: LineUpPlayerProps) => {
  const stringifiedCards = useMemo(() => JSON.stringify(cards ?? []), [cards]);

  const nbGoalsAnimationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const nbOwnGoalsAnimationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const enterTimerAnimationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const leaveTimerAnimationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const cardsAnimationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [hasNbGoalsJustChanged, setHasNbGoalsJustChanged] = useState(false);
  const [hasNbOwnGoalsJustChanged, setHasNbOwnGoalsJustChanged] = useState(false);
  const [hasEnterTimerJustChanged, setHasEnterTimerJustChanged] = useState(false);
  const [hasLeaveTimerJustChanged, setHasLeaveTimerJustChanged] = useState(false);
  const [hasCardsJustChanged, setHasCardsJustChanged] = useState(false);

  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) {
      const timeoutRef = nbGoalsAnimationTimeoutRef;
      animateOnChange(timeoutRef, setHasNbGoalsJustChanged);
    }
  }, [nbGoals]);

  useEffect(() => {
    if (initialized.current) {
      const timeoutRef = nbOwnGoalsAnimationTimeoutRef;
      animateOnChange(timeoutRef, setHasNbOwnGoalsJustChanged);
    }
  }, [nbOwnGoals]);

  useEffect(() => {
    if (initialized.current) {
      const timeoutRef = enterTimerAnimationTimeoutRef;
      animateOnChange(timeoutRef, setHasEnterTimerJustChanged);
    }
  }, [enterTimer]);

  useEffect(() => {
    if (initialized.current) {
      const timeoutRef = leaveTimerAnimationTimeoutRef;
      animateOnChange(timeoutRef, setHasLeaveTimerJustChanged);
    }
  }, [leaveTimer]);

  useEffect(() => {
    if (initialized.current) {
      const timeoutRef = cardsAnimationTimeoutRef;
      animateOnChange(timeoutRef, setHasCardsJustChanged);
    }
  }, [stringifiedCards, name]);

  useEffect(() => {
    initialized.current = true;
  }, []);

  useEffect(() => {
    return () => {
      const nbGoalsAnimationTimeout = nbGoalsAnimationTimeoutRef.current;
      const nbOwnGoalsAnimationTimeout = nbOwnGoalsAnimationTimeoutRef.current;
      const enterTimerAnimationTimeout = enterTimerAnimationTimeoutRef.current;
      const leaveTimerAnimationTimeout = leaveTimerAnimationTimeoutRef.current;
      const cardsAnimationTimeout = cardsAnimationTimeoutRef.current;

      if (nbGoalsAnimationTimeout) {
        clearTimeout(nbGoalsAnimationTimeout);
        nbGoalsAnimationTimeoutRef.current = null;
      }
      if (nbOwnGoalsAnimationTimeout) {
        clearTimeout(nbOwnGoalsAnimationTimeout);
        nbOwnGoalsAnimationTimeoutRef.current = null;
      }
      if (enterTimerAnimationTimeout) {
        clearTimeout(enterTimerAnimationTimeout);
        enterTimerAnimationTimeoutRef.current = null;
      }
      if (leaveTimerAnimationTimeout) {
        clearTimeout(leaveTimerAnimationTimeout);
        leaveTimerAnimationTimeoutRef.current = null;
      }
      if (cardsAnimationTimeout) {
        clearTimeout(cardsAnimationTimeout);
        cardsAnimationTimeoutRef.current = null;
      }
    };
  }, []);

  const [parentTriggerer, setParentTriggerer] = useState(null);

  const onClick = useCallback(() => {
    setParentTriggerer(v4());
  }, []);

  const forwardedValue = useMemo(() => ({ parentTriggerer }), [parentTriggerer]);

  const nameSize = useMemo(() => {
    if (name?.length > 13) {
      return LineUpPlayerJerseySize.small;
    }
    if (name?.length > 10) {
      return LineUpPlayerJerseySize.medium;
    }
    return LineUpPlayerJerseySize.large;
  }, [name]);

  const jerseySize = useMemo(() => JerseySizesBySportId[sportId] ?? LineUpPlayerJerseySize.medium, [sportId]);

  const playerCardIcon = useMemo(() => {
    const isMyMatch = matchViewMode === MatchViewModes.MY_MATCH;

    switch (sportId) {
      case SportIds.RUGBY:
      case SportIds.RUGBY_LEAGUE:
      case SportIds.BASEBALL:
        return <StyledRugbyPlayerCardIcon selected={selected} isMyMatch={isMyMatch} />;
      case SportIds.SOCCER:
      default:
        return <StyledSoccerPlayerCardIcon selected={selected} isMyMatch={isMyMatch} />;
    }
  }, [matchViewMode, sportId, selected]);

  return (
    <Wrapper className={className} $isDisabled={!canSelect} onClick={onClick} $jerseySize={jerseySize}>
      {playerCardIcon}
      <LineUpPlayerStatsContainer />
      <PlayerArea>
        <JerseyRow>
          {enterTimer || leaveTimer ? (
            <SubstitutionInfosArea>
              {enterTimer ? (
                <SubstitutionTimerBlock $hasJustChanged={hasEnterTimerJustChanged}>
                  <SubstitutionTimerLabel>{enterTimer}</SubstitutionTimerLabel>
                  <StyledLineUpArrowInIconSvg />
                </SubstitutionTimerBlock>
              ) : null}
              {leaveTimer ? (
                <SubstitutionTimerBlock $hasJustChanged={hasLeaveTimerJustChanged}>
                  <SubstitutionTimerLabel>{leaveTimer}</SubstitutionTimerLabel>
                  <StyledLineUpArrowOutIconSvg />
                </SubstitutionTimerBlock>
              ) : null}
            </SubstitutionInfosArea>
          ) : null}
          <StyledLineUpPlayerJerseyContainer size={jerseySize}>
            {Array.isArray(cards) && cards.length ? (
              <CardsWrapper $hasJustChanged={hasCardsJustChanged} $size={jerseySize}>
                {cards.map((cardType, i) => (
                  <Card key={`${cardType}-${i}`} cardType={cardType} index={i} nbCards={cards.length} />
                ))}
              </CardsWrapper>
            ) : null}
          </StyledLineUpPlayerJerseyContainer>
          {nbOwnGoals || nbGoals ? (
            <LineUpGoalsInfo
              sportId={sportId}
              nbGoals={nbGoals}
              nbOwnGoals={nbOwnGoals}
              hasNbGoalsJustChanged={hasNbGoalsJustChanged}
              hasNbOwnGoalsJustChanged={hasNbOwnGoalsJustChanged}
            />
          ) : null}
        </JerseyRow>
        {name ? (
          <PlayerWrapper $nameSize={nameSize}>
            <PlayerName>{name}</PlayerName>
          </PlayerWrapper>
        ) : null}
        <ParentTriggererContext.Provider value={forwardedValue}>
          <StyledLineUpPlayerOddsContainer />
        </ParentTriggererContext.Provider>
      </PlayerArea>
    </Wrapper>
  );
};

export default LineUpPlayer;
